import { Icon, useToast } from '@/src/components/ui';
import { Button } from '@workspace/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { cn } from '@workspace/ui/lib/utils';
import React from 'react';
import { ISocialPost, ISocialPostComment } from 'types/src/types';
import { Report } from '../../bits/components/Report';
import { useDeletePostMutation } from '@/src/hooks/use-post-mutations';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useDeleteSocialPostCommentMutation } from '@/src/hooks/use-comment-mutation';
import { useRouter } from 'next/navigation';

type Props = {
  post?: ISocialPost;
  isMyPost?: boolean;
  comment?: ISocialPostComment;
  postComment?: ISocialPostComment;
  isMyComment?: boolean;
};

export const DotsButton = ({
  post,
  isMyPost,
  comment,
  postComment,
  isMyComment,
}: Props) => {
  const deleteMutation = useDeletePostMutation();
  const { mutateAsync: deletePostComment } =
    useDeleteSocialPostCommentMutation();
  const toast = useToast();
  const router = useRouter();
  const menuOpen = useBoolean(false);
  const reportOpen = useBoolean(false);

  const handleDelete = async () => {
    try {
      if (post) {
        await deleteMutation.mutateAsync(post.id);
        router.push('/');
      } else if (comment) {
        await deleteMutation.mutateAsync(comment.id);
      } else if (postComment) {
        await deletePostComment(postComment.id);
      }
      toast.success('Deleted');
      menuOpen.onFalse();
    } catch (e) {
      toast.error('Failed to delete post');
    }
  };

  const openReport = () => {
    menuOpen.onFalse();
    setTimeout(() => reportOpen.onTrue(), 0);
  };

  return (
    <>
      <DropdownMenu
        modal={false}
        open={menuOpen.value}
        onOpenChange={menuOpen.setValue}
      >
        <DropdownMenuTrigger asChild>
          <Button
            data-no-card-click="true"
            variant="ghost"
            size="sm"
            className={cn(
              'text-blue-gray hover:bg-transparent hover:text-pink-accent h-2! py-3!'
            )}
          >
            <Icon name="dots" className="w-4 h-1 rotate-90" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="center" side="top">
          {(isMyPost || isMyComment) && (
            <DropdownMenuItem
              className="flex items-center py-0 px-1 cursor-pointer"
              data-no-card-click="true"
            >
              <Button
                data-no-card-click="true"
                variant="outline"
                size="sm"
                className="w-fit h-fit border-0 gap-2 md:py-1 bg-transparent flex items-center font-medium text-sm justify-start shadow-none"
                onClick={handleDelete}
              >
                <Icon name="delete" className="size-3.5 text-red-error" />
                Delete
              </Button>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="flex items-center md:py-1 px-1 cursor-pointer font-medium"
            onClick={openReport}
            data-no-card-click="true"
          >
            <Icon name="report" className="size-3.5 text-red-error" />
            Report
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Report
        data-no-card-click="true"
        type={post ? 'post' : 'postComment'}
        socialPostId={post?.id}
        socialCommentId={postComment?.id}
        hideTrigger
        open={reportOpen.value}
        onOpenChange={(o) => {
          reportOpen.setValue(o);
          if (!o) menuOpen.onFalse();
        }}
      />
    </>
  );
};
